import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { default as Form } from '../../components/connectedForm';
import OverviewContainer from '../../components/connectedForm/OverviewContainer';
import OverviewCard from '../../components/connectedForm/OverviewCard';
import OverviewCardTitle from '../../components/connectedForm/OverviewCardTitle';
import OverviewCardDivider from '../../components/connectedForm/OverviewCardDivider';
import OverviewCardSubTitle from '../../components/connectedForm/OverviewCardSubTitle';
import OverviewField from '../../components/connectedForm/OverviewField';
import * as input from '../../components/connectedForm/inputComponents';
import OverviewInlineField from '../../components/connectedForm/OverviewInlineField';
import getIn from '../../components/connectedForm/utils/getIn';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import SalesInvoiceYearToDateCombinedQuery from '../../graphql/queries/salesLedger/SalesInvoiceYearToDateCombinedQuery.graphql';
import StockTotalsQuery from '../../graphql/queries/product/StockTotalsQuery.graphql';
import styled from 'styled-components';
import { useCompany, useProductConfig } from '../../utils/FetchConfig';
import CostField from '../../components/CostField';
import SalesInvoiceAnalysisCard from '../salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysisCard';
import { maxMakeable } from '../../graphql/entities/product/Product';
import {
  CostViewPermission,
  ICurrency,
  IProduct,
  ISalesInvoiceYearToDateCombinedQueryResult,
  ISalesInvoiceYearToDateCombinedQueryVariables,
  IStockTotalsQueryResult,
  IStockTotalsQueryVariables,
  IWarehouse,
  ProductClass,
} from '../../graphql/schema/p4types';
import { EmbeddedSalesInvoiceOptions } from '../salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysis';
import { checkPermission } from '../../utils/OperationPermissions';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { useFullUserDetails, usePrecision } from '../../utils/hooks';
import AppContext from '../../components/Context/AppContext';
import CurrencyContext from '../../components/Context/CurrencyContext';
import HeaderStatus from '../../components/connectedForm/HeaderStatus';
import { makeStyles } from '@fluentui/react-components';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { Maybe } from 'graphql/jsutils/Maybe';
import { mdiInformationOutline } from '@mdi/js';
import { useCurrentFinancialYear } from '../../utils/company/useFinancialCalendars';

interface IProductOverviewTabProps {
  id: string;
  values: IProduct;
  sidebar?: boolean;
  tabPanelRef?: React.RefObject<HTMLElement>;
  history?: any;
}

interface IProductWarehouseStock {
  level: number;
  free: number;
  onOrder: number;
  outstanding: number;
  awaitingDispatch: number;
  forecasted: number;
  warehouse?: Maybe<IWarehouse>[];
}

const StockStatus = styled.div<{ type: 'ok' | 'warn' | 'danger' }>`
  padding: 1px 8px;
  border-radius: 3px;
  color: white;
  display: block;
  font-weight: bold;
  margin: 0;
  text-align: center;
  font-size: 11px;
  background-color: ${(props) =>
    props.type === 'ok'
      ? p4Tokens.statusFlagSuccess
      : props.type === 'warn'
      ? p4Tokens.statusFlagWarning
      : p4Tokens.statusFlagDanger};
`;

const useStyles = makeStyles({
  currencyBadge: {
    marginLeft: '5px',
  },
});

const CurrencyBadge = ({
  mainSupplierCurrencyAlpha3,
  baseCurrencyAlpha3,
}: {
  mainSupplierCurrencyAlpha3: Maybe<string>;
  baseCurrencyAlpha3: Maybe<string>;
}) => {
  const styles = useStyles();

  return mainSupplierCurrencyAlpha3 && baseCurrencyAlpha3 && mainSupplierCurrencyAlpha3 !== baseCurrencyAlpha3 ? (
    <span className={styles.currencyBadge}>
      <HeaderStatus icon="account_balance" title={mainSupplierCurrencyAlpha3} statusStyle="basic" />
    </span>
  ) : null;
};

const ProductOverviewTab = ({ id, values, sidebar, tabPanelRef, history }: IProductOverviewTabProps) => {
  const [stock, setStock] = useState<IProductWarehouseStock>({
    level: 0,
    free: 0,
    onOrder: 0,
    outstanding: 0,
    awaitingDispatch: 0,
    forecasted: 0,
    warehouse: [],
  });

  const [predefinedAnalysisFilters, setPredefinedAnalysisFilters] = useState<{ dimension: string; id: string }[]>([]);
  const [costCurrency, setCostCurrency] = useState<Maybe<ICurrency>>();

  const handleQueryError = useComponentQueryErrorHandling();

  const [fetchSalesData, { data: salesData }] = useLazyQuery<
    ISalesInvoiceYearToDateCombinedQueryResult,
    ISalesInvoiceYearToDateCombinedQueryVariables
  >(SalesInvoiceYearToDateCombinedQuery, {
    onError: handleQueryError,
  });

  const { statSales, statProfit, statUnit } = useMemo(() => {
    if (salesData) {
      return {
        statSales: salesData.data?.[0]?.value ?? 0,
        statProfit: salesData.data2?.[0]?.value ?? 0,
        statUnit: salesData.data3?.[0]?.value ?? 0,
      };
    }
    return {
      statSales: 0,
      statProfit: 0,
      statUnit: 0,
    };
  }, [salesData]);

  const [fetchStockData, { data: stockData }] = useLazyQuery<IStockTotalsQueryResult, IStockTotalsQueryVariables>(
    StockTotalsQuery,
    {
      onError: handleQueryError,
    }
  );

  useEffect(() => {
    if (stockData) {
      setStock(getStockLevel(stockData));
    }
  }, [stockData]);

  const company = useCompany();
  const fullUserDetails = useFullUserDetails();
  const app = useContext(AppContext);
  const productConfig = useProductConfig();
  const isHaggle = fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.Haggle;
  const costPrecision = usePrecision('cost');
  const baseCurrencyAlpha3 = company?.baseCurrency?.isoCode?.codeAlpha3;
  const mainSupplierCurrencyAlpha3 = values.mainSupplierDetails?.supplier?.currency?.isoCode?.codeAlpha3;

  const currency =
    mainSupplierCurrencyAlpha3 && mainSupplierCurrencyAlpha3 !== baseCurrencyAlpha3
      ? values.mainSupplierDetails?.supplier?.currency
      : company?.baseCurrency;

  const current = useCurrentFinancialYear();
  const currentCalendar = current ?? company?.currentFinancialYear;

  useEffect(() => {
    if (id) {
      setPredefinedAnalysisFilters([{ dimension: 'PRODUCT', id: id }]);
      const salesVariables = normalizeVariables({
        measure: 'SALES',
        measure2: 'PROFIT',
        measure3: 'UNIT',
        financialCalendar: currentCalendar,
        dimensionFilters: [{ dimension: 'PRODUCT', id: id }],
      });

      const stockVariables = normalizeVariables({ productId: id });

      fetchSalesData({ variables: salesVariables });
      fetchStockData({ variables: stockVariables });
    }
  }, [id, fetchSalesData, fetchStockData, company]);

  useEffect(() => {
    setCostCurrency(values.mainSupplierDetails?.costsInCurrency ? currency : company?.baseCurrency);
  }, [company?.baseCurrency, currency, values.mainSupplierDetails?.costsInCurrency]);

  const getStockLevel = (stock: IStockTotalsQueryResult) => {
    const data = {
      level: 0,
      free: stock?.data?.[0]?.product?.freeStock || 0,
      onOrder: 0,
      outstanding: 0,
      awaitingDispatch: 0,
      forecasted: 0,
      warehouse: stock && stock.data,
    };
    if (stock && Array.isArray(stock.data)) {
      stock.data.forEach((stockItem) => {
        data.level += stockItem?.level ?? 0;
        data.onOrder += stockItem?.onOrder ?? 0;
        data.outstanding += stockItem?.outstanding ?? 0;
        data.awaitingDispatch += stockItem?.awaitingDispatch ?? 0;
        data.forecasted += stockItem?.forecasted ?? 0;
      });
    }
    return data;
  };

  const getWarehouseStock = (stockData: IProductWarehouseStock, dp: number) => {
    const productWarehouseDetailsByWarehouse = values?.productWarehouseDetails?.reduce((map, obj) => {
      if (obj?.warehouse?.id) {
        map[obj.warehouse.id] = obj;
      }
      return map;
    }, {} as { [key: string]: any });

    return stockData?.warehouse?.map((wh: any, index: number) => {
      const pwd = productWarehouseDetailsByWarehouse?.[wh.warehouse.id];
      return (
        <OverviewInlineField
          key={wh.id}
          label={wh.warehouse.description}
          labelWidth={200}
          labelCols={3 / 4}
          inputCols={1 / 4}
          labelAddon={pwd?.defaultBinLocation?.location}
          value={wh.level}
          decimals={dp}
        />
      );
    });
  };

  const getStockStatus = (stockData: IProductWarehouseStock) => {
    let status: JSX.Element;
    if (stockData.free > 0) {
      status = <StockStatus type="ok">In stock</StockStatus>;
    } else if (stockData.level > 0) {
      status = <StockStatus type="warn">No free stock</StockStatus>;
    } else {
      status = <StockStatus type="danger">Out of stock</StockStatus>;
    }
    return <>Availability {status}</>;
  };

  const renderSellingPrice = (idx: number) => {
    if (!productConfig) return null;
    const { sellingPrices } = values;
    const title = productConfig[`sellingPriceTitle${idx}`];
    if (!title) return null;
    const price = getIn(sellingPrices, `sellingPrice${idx}`);
    return <OverviewInlineField label={title} value={price} type={'baseCurrency'} valueType={'sales'} />;
  };

  const isKitOrAssembly = values?.productClass === ProductClass.Kit || values?.productClass === ProductClass.Assembly;
  const isProcess = values?.productClass === 'PROCESS';
  const warehouseStock = !isProcess ? getWarehouseStock(stock, values.precision!) : null;
  const status = !isProcess ? getStockStatus(stock) : null;

  const cols = sidebar ? 1 : 1 / 3;
  const { mainSupplierDetails, sellingPrices } = values;

  const canViewProfit =
    fullUserDetails?.companyUser?.costViewPermission &&
    fullUserDetails.companyUser.costViewPermission === CostViewPermission.All;

  const suppliersPermitted = checkPermission({ app }, `SUPPLIERS_VIEW`);
  const salesAnalysisPermitted = checkPermission({ app }, `SALES_LEDGER_ANALYTICS`);
  const overViewTitle = `Sales Year to Date (${currentCalendar?.year})`;

  const infoIcon = {
    path: mdiInformationOutline,
    color: p4Tokens.colorInfoIcon,
  };

  return (
    <OverviewContainer sidebar={sidebar}>
      <Form.Row>
        <CurrencyContext.Provider
          value={{
            currency: costCurrency,
          }}
        >
          <Form.Fieldset cols={cols}>
            <OverviewCard>
              <OverviewCardTitle
                title="Product information"
                linkTabLabel="Details"
                linkTabId="main"
                modal={sidebar}
                type="product"
                entityId={id}
                history={history}
                tabPanelRef={tabPanelRef}
              />
              <OverviewField value={values.reference} list={true} primary={true} type="text" />
              <OverviewField value={values.description} list={true} primary={true} type="text" />
              <OverviewInlineField label="Barcode" value={values.barcode} type="text" />
              <OverviewInlineField label="Commodity" value={values.commodityCode} type="text" />
              {suppliersPermitted ? (
                <>
                  <OverviewCardDivider />
                  <OverviewCardSubTitle title="Main Supplier" />
                  <OverviewField
                    value={
                      <>
                        {mainSupplierDetails && mainSupplierDetails.supplier?.name}
                        <CurrencyBadge
                          baseCurrencyAlpha3={baseCurrencyAlpha3}
                          mainSupplierCurrencyAlpha3={mainSupplierCurrencyAlpha3}
                        />
                      </>
                    }
                    list={true}
                    type="text"
                  />
                  <CostField isHaggle={isHaggle}>
                    <OverviewInlineField
                      label="Cost price"
                      value={mainSupplierDetails?.costPrices?.costPrice1}
                      type={'currency'}
                      decimals={costPrecision}
                    />
                  </CostField>
                  <OverviewInlineField
                    label="Lead time days"
                    value={mainSupplierDetails && mainSupplierDetails.leadTimeDays}
                    type="text"
                  />
                </>
              ) : null}
            </OverviewCard>
            <OverviewCard>
              <OverviewCardTitle
                title="Cost/Selling Prices"
                linkTabId="prices"
                linkTabLabel="View"
                tabPanelRef={tabPanelRef}
              />
              <OverviewInlineField
                label="VAT category"
                value={values.vatProductCategory && values.vatProductCategory.description}
                type="text"
              />
              <OverviewInlineField
                label="Valuation method"
                value={values.valuationMethod}
                type="text"
                initialCaps={true}
              />
              <CostField isHaggle={isHaggle}>
                <OverviewCardSubTitle
                  title={`Costs per ${values.costUnitQuantity ? values.costUnitQuantity : ''} (${
                    values.costUnitDescription ? values.costUnitDescription : ''
                  })`}
                />
                <OverviewInlineField
                  label="Valuation"
                  value={values.valuationCostPrice}
                  type={'baseCurrency'}
                  valueType={'cost'}
                />
                <OverviewInlineField
                  label="Latest"
                  value={values.latestCostPrice}
                  type={'baseCurrency'}
                  valueType={'cost'}
                />
              </CostField>
              <OverviewCardDivider />
              <OverviewCardSubTitle
                title={`Selling prices per ${values.sellingUnitQuantity ? values.sellingUnitQuantity : ''} (${
                  values.sellingUnitDescription ? values.sellingUnitDescription : ''
                })`}
              />
              <OverviewInlineField
                label="Minimum"
                value={sellingPrices && sellingPrices.minimumPrice}
                type={'baseCurrency'}
                valueType={'sales'}
              />
              {renderSellingPrice(1)}
              {renderSellingPrice(2)}
              {renderSellingPrice(3)}
              {renderSellingPrice(4)}
              {renderSellingPrice(5)}
              {renderSellingPrice(6)}
              {renderSellingPrice(7)}
              {renderSellingPrice(8)}
              {renderSellingPrice(9)}
              {renderSellingPrice(10)}
            </OverviewCard>
          </Form.Fieldset>

          <Form.Fieldset cols={sidebar ? 1 : 2 / 3}>
            {salesAnalysisPermitted && !sidebar ? (
              <Form.Row>
                {
                  <SalesInvoiceAnalysisCard
                    predefinedFilters={predefinedAnalysisFilters}
                    ready={!!predefinedAnalysisFilters}
                    embedded={EmbeddedSalesInvoiceOptions.Product}
                    defaultLabel={values.reference}
                    graphRightClickEnabled={true}
                    cardHeader={
                      <>
                        <OverviewCardTitle
                          title={overViewTitle}
                          linkTabId="analytics"
                          linkTabLabel="Analytics"
                          tabPanelRef={tabPanelRef}
                          hint={'Right click on graph for additional options.'}
                        />
                        <Form.Row>
                          <Form.Fieldset cols={canViewProfit ? 1 / 3 : 1 / 2}>
                            <OverviewInlineField
                              label="Sales"
                              value={statSales}
                              decimals={2}
                              style={{
                                borderRight: '#cacaca',
                                borderRightWidth: 2,
                                borderRightStyle: 'solid',
                              }}
                              type={'baseCurrency'}
                            />
                          </Form.Fieldset>

                          {canViewProfit ? (
                            <Form.Fieldset cols={1 / 3}>
                              <OverviewInlineField
                                label="Profit"
                                value={statProfit}
                                decimals={2}
                                style={{
                                  borderRight: '#cacaca',
                                  borderRightWidth: 2,
                                  borderRightStyle: 'solid',
                                }}
                                type={'baseCurrency'}
                              />
                            </Form.Fieldset>
                          ) : null}

                          <Form.Fieldset cols={canViewProfit ? 1 / 3 : 1 / 2}>
                            <OverviewInlineField label="Units" value={statUnit} decimals={2} />
                          </Form.Fieldset>
                        </Form.Row>
                      </>
                    }
                  />
                }
              </Form.Row>
            ) : null}

            {!isProcess ? (
              <Form.Row>
                <Form.Fieldset cols={sidebar ? 1 : 1 / 2}>
                  <OverviewCard>
                    <OverviewCardTitle
                      title={status}
                      linkTabId="productAvailability"
                      linkTabLabel="View"
                      tabPanelRef={tabPanelRef}
                    />
                    <OverviewInlineField label="Level" value={stock?.level} decimals={values.precision} />
                    <OverviewInlineField label="Free" value={stock?.free} decimals={values.precision} />
                    <OverviewInlineField label="On order" value={stock?.onOrder} decimals={values.precision} />
                    <OverviewInlineField
                      label="Outstanding"
                      value={stock?.outstanding}
                      decimals={values.precision}
                      icon={infoIcon}
                      hint={'Stock Awaiting Allocation (Excludes Assigned)'}
                    />
                    <OverviewInlineField
                      label="Awaiting Dispatch"
                      value={stock?.awaitingDispatch}
                      decimals={values.precision}
                      hint={'Stock Awaiting Allocation + Allocated + Assigned'}
                      icon={infoIcon}
                    />
                    <OverviewInlineField label="Forecasted" value={stock?.forecasted} decimals={values.precision} />
                    {isKitOrAssembly ? (
                      <OverviewInlineField
                        label="Makeable"
                        value={values.makeable! > maxMakeable ? '∞' : values.makeable}
                        decimals={values.precision}
                      />
                    ) : null}
                    <OverviewInlineField label="In Assembly" value={values.inAssembly} decimals={values.precision} />
                    <OverviewCardDivider />
                    <OverviewCardSubTitle title="Warehouse Stock" />
                    {warehouseStock}
                  </OverviewCard>
                </Form.Fieldset>
                <Form.Fieldset cols={1 / 2}>
                  {!sidebar && values?.image?.fileName ? (
                    <OverviewCard>
                      <Form.Field
                        dataid="image.fileName"
                        component={input.ImageSelector}
                        includeLabel={false}
                        readOnly={true}
                      />
                    </OverviewCard>
                  ) : null}
                </Form.Fieldset>
              </Form.Row>
            ) : null}
          </Form.Fieldset>
        </CurrencyContext.Provider>
      </Form.Row>
    </OverviewContainer>
  );
};

const ProductOverviewTabWrapper = (props) => {
  const fullUserDetails = useFullUserDetails();
  return <ProductOverviewTab {...props} app={{ fullUserDetails }} />;
};

export default ProductOverviewTabWrapper;
